import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DebouncedInput, DebouncedTextarea } from '../ui/debounced-textarea';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { searchMedications, MEDICATION_CATEGORIES } from '../../constants/psychotropicMedications';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AddEditMedicationDialog - Dialog for adding or editing a client medication
 * 
 * @param {boolean} open - Whether dialog is open
 * @param {function} onOpenChange - Callback when open state changes
 * @param {object} client - The client to add/edit medication for (needs id and full_name)
 * @param {object|null} editingMedication - If editing, the medication object; null for new
 * @param {function} onSuccess - Callback after successful save (receives clientId and options)
 */
export const AddEditMedicationDialog = ({ 
  open, 
  onOpenChange, 
  client, 
  editingMedication = null,
  onSuccess,
  // Jun 2026 — Medication Plan capture mode: when provided (and not
  // editing), the form result is routed to onCapture(formData) instead
  // of being saved to the client's active medication list.
  onCapture = null
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    medication_name: '',
    dosage: '',
    frequency: '',
    prescriber: '',
    purpose: '',
    start_date: new Date().toISOString().slice(0, 10),
    notes: ''
  });
  
  // Medication autocomplete state
  const [medSuggestions, setMedSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);

  // Populate form when editing
  useEffect(() => {
    if (editingMedication) {
      setFormData({
        medication_name: editingMedication.medication_name || '',
        dosage: editingMedication.dosage || '',
        frequency: editingMedication.frequency || '',
        prescriber: editingMedication.prescriber || '',
        purpose: editingMedication.purpose || '',
        start_date: editingMedication.start_date 
          ? new Date(editingMedication.start_date).toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10),
        notes: editingMedication.notes || ''
      });
    } else {
      resetForm();
    }
  }, [editingMedication, open]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resetForm = () => {
    setFormData({
      medication_name: '',
      dosage: '',
      frequency: '',
      prescriber: '',
      purpose: '',
      start_date: new Date().toISOString().slice(0, 10),
      notes: ''
    });
    setMedSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  // Handle medication name input change with autocomplete
  const handleMedNameChange = (value) => {
    setFormData(prev => ({ ...prev, medication_name: value }));
    
    if (value.length >= 2) {
      const suggestions = searchMedications(value, 8);
      setMedSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      setHighlightedIndex(-1);
    } else {
      setMedSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle selecting a medication from suggestions
  const handleSelectMedication = (med) => {
    // Set the medication name and prefill category/purpose
    setFormData(prev => ({
      ...prev,
      medication_name: `${med.generic} (${med.brand})`,
      purpose: med.category // Prefill the purpose field with category
    }));
    setShowSuggestions(false);
    setMedSuggestions([]);
  };

  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || medSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev < medSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelectMedication(medSuggestions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (onCapture && !editingMedication) {
      onCapture({ ...formData });
      onOpenChange(false);
      resetForm();
      return;
    }
    if (!client?.id) {
      toast.error("No client selected");
      return;
    }
    
    setLoading(true);
    let createdMedication = null;
    try {
      if (editingMedication) {
        // Update existing medication
        await axios.patch(`${API}/api/medications/${editingMedication.id}`, formData);
        toast.success("Medication updated successfully");
      } else {
        // Add new medication
        const res = await axios.post(`${API}/api/clients/${client.id}/medications`, formData);
        toast.success("Medication added successfully");
        createdMedication = res.data;
      }
      
      onOpenChange(false);
      resetForm();
      
      // Trigger parent to refresh client data - stay on medications tab
      if (onSuccess) {
        onSuccess(client.id, { initialTab: 'medications', createdMedication });
      }
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(error.response.data?.detail || "This medication is already active for this client");
      } else {
        toast.error(`Failed to ${editingMedication ? 'update' : 'add'} medication`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        resetForm();
      }
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {onCapture && !editingMedication
              ? `Add Medication to Plan${client?.full_name ? ` - ${client.full_name}` : ''}`
              : `${editingMedication ? 'Edit' : 'Add'} Medication - ${client?.full_name || ''}`}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Row 1: Medication Name (full width for dropdown) */}
          <div className="relative">
            <Label htmlFor="medication_name">Medication Name *</Label>
            <Input
              ref={inputRef}
              id="medication_name"
              value={formData.medication_name}
              onChange={(e) => handleMedNameChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (medSuggestions.length > 0) setShowSuggestions(true);
              }}
              required
              placeholder="Start typing medication name (e.g., sertraline, Zoloft, metformin)..."
              autoComplete="off"
              data-testid="medication-name-input"
            />
            {/* Medication Suggestions Dropdown - Full Width */}
            {showSuggestions && medSuggestions.length > 0 && (
              <div 
                ref={suggestionsRef}
                className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto"
              >
                <div className="px-3 py-2 bg-gray-50 border-b text-xs text-gray-500 font-medium">
                  Common Medications Reference — Select to auto-fill
                </div>
                {medSuggestions.map((med, idx) => (
                  <div
                    key={`${med.generic}-${med.brand}-${idx}`}
                    className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                      highlightedIndex === idx ? 'bg-teal-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => handleSelectMedication(med)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">{med.generic}</span>
                          <span className="text-gray-500">({med.brand})</span>
                        </div>
                        <div className="text-sm text-teal-600 mt-1">
                          Usual dosage: <strong>{med.dosage}</strong>
                        </div>
                      </div>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded whitespace-nowrap flex-shrink-0">
                        {med.category}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Row 2: Dosage and Frequency */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="dosage">Dosage *</Label>
              <DebouncedInput
                id="dosage"
                value={formData.dosage}
                onChange={(e) => setFormData(prev => ({...prev, dosage: e.target.value}))}
                required
                placeholder="e.g., 50mg, 10mg twice daily"
                data-testid="medication-dosage-input"
              />
            </div>
            <div>
              <Label htmlFor="frequency">Frequency *</Label>
              <DebouncedInput
                id="frequency"
                value={formData.frequency}
                onChange={(e) => setFormData(prev => ({...prev, frequency: e.target.value}))}
                required
                placeholder="e.g., Once daily, BID, PRN"
                data-testid="medication-frequency-input"
              />
            </div>
          </div>
          
          {/* Row 3: Prescriber and Purpose */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="prescriber">Prescriber *</Label>
              <Input
                id="prescriber"
                value={formData.prescriber}
                onChange={(e) => setFormData(prev => ({...prev, prescriber: e.target.value}))}
                required
                placeholder="e.g., Dr. Smith, PCP"
                data-testid="medication-prescriber-input"
              />
            </div>
            <div>
              <Label htmlFor="purpose">Purpose / Category</Label>
              <DebouncedInput
                id="purpose"
                value={formData.purpose}
                onChange={(e) => setFormData(prev => ({...prev, purpose: e.target.value}))}
                placeholder="e.g., Antidepressant, Blood Pressure"
                list="purpose-suggestions"
                data-testid="medication-purpose-input"
              />
              <datalist id="purpose-suggestions">
                {MEDICATION_CATEGORIES.map((cat, idx) => (
                  <option key={idx} value={cat} />
                ))}
              </datalist>
            </div>
          </div>
          
          {/* Row 4: Start Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Start Date *</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({...prev, start_date: e.target.value}))}
                required
                data-testid="medication-start-date-input"
              />
            </div>
          </div>
          
          {/* Row 5: Notes */}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <DebouncedTextarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
              placeholder="Additional notes about this medication (side effects, interactions, special instructions)"
              rows={2}
              data-testid="medication-notes-input"
            />
          </div>
          
          <div className="flex justify-end space-x-2 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              data-testid="medication-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-teal-600 hover:bg-teal-700"
              disabled={loading}
              data-testid="medication-submit-btn"
            >
              {loading ? 'Saving...' : (onCapture && !editingMedication ? 'Add to Plan' : `${editingMedication ? 'Update' : 'Add'} Medication`)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditMedicationDialog;
